import { ModuleManager } from "./ModuleManager";
import { StringData } from "../StringData";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EventManager } from "./EventManager";
import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { StringToolsContainer } from "../StringToolsContainer";
import { DOMBatcher } from "./DOMBatcher";
import { StringModule } from "../StringModule";
import { StringToken } from "../../models/string/StringToken";
import { ScrollMode } from "../../states/ScrollState";

export class ObjectManager {
  private objects = new Map<string, StringObject>();
  private connectQueue: { id: string; element: HTMLElement }[] = [];
  private connectableModulesBuffer: StringModule[] = [];
  private mirrors = new Map<string, StringMirrorObject>();
  private mirrorId = 1;
  private globalId = 1;
  private domBatcher = new DOMBatcher();
  private domBatcherEnabled = false;
  private inviewStarts: Array<{ pos: number; object: StringObject }> = [];
  private inviewEnds: Array<{ pos: number; object: StringObject }> = [];
  private inviewActive = new Set<StringObject>();
  private inviewStartIdx = 0;
  private inviewEndIdx = 0;
  private inviewIndexDirty = true;
  private lastInviewScrollPos = 0;
  private intersectionObserverEnabled = true;
  private domObserver: MutationObserver | null = null;
  private static readonly DEVTOOLS_ARTIFACT_SELECTOR =
    "[data-stdg],[data-string-dev-viewport-layer],[data-string-dev-viewport-world]";

  /**
   * Sub-pixel tolerance for the active-range test. Layout metrics are
   * fractional while the reachable scroll maximum is integral, so without
   * this an object can fall out of range in the final pixel of its span.
   */
  private static readonly ACTIVE_RANGE_EPSILON = 1;

  constructor(
    private data: StringData,
    private modules: ModuleManager,
    private events: EventManager,
    private tools: StringToolsContainer
  ) {}

  /**
   * Returns the object map (read-only).
   */
  get all(): ReadonlyMap<string, StringObject> {
    return this.objects;
  }

  /**
   * Adds a new object from an element.
   */
  public add(el: HTMLElement) {
    let idAttr = `string-${this.globalId++}`;
    let key = "string-id";

    if (el.getAttribute("string-id")) {
      idAttr = el.getAttribute("string-id")!;
      key = "string-id";
    }
    if (el.getAttribute("data-string-id")) {
      idAttr = el.getAttribute("data-string-id")!;
      key = "data-string-id";
    }

    const object =
      idAttr && this.objects.has(idAttr) ? this.objects.get(idAttr)! : new StringObject(idAttr, el);

    el.setAttribute(key, object.id);

    const keysAttr = el.getAttribute("string") ?? el.getAttribute("data-string");

    if (keysAttr) {
      object.setTokens(this.parseStringTokens(keysAttr));
    }

    el.setAttribute("string-inited", "");
    this.objects.set(object.id, object);

    const attributes = this.getAllAttributes(el);

    // Delegate core setup (dimensions, offsets, key, start/end, etc.)
    const coreModules = this.modules.core;
    for (let i = 0; i < coreModules.length; i++) {
      const m = coreModules[i] as any;
      if ("setupCoreProperties" in m && typeof m["setupCoreProperties"] === "function") {
        m.setupCoreProperties(object, el, attributes);
      }
    }

    // Try connecting to modules
    const connectableModules = this.connectableModulesBuffer;
    connectableModules.length = 0;
    const allModules = this.modules.all;
    let inviewFallbackModule: StringModule | null = null;
    for (let i = 0; i < allModules.length; i++) {
      const m = allModules[i];
      if (m instanceof StringModule && m.key === "") {
        inviewFallbackModule = m;
      }
      if (m instanceof StringModule && m.canConnect(object)) {
        connectableModules.push(m);
      }
    }
    if (connectableModules.length === 0 && inviewFallbackModule) {
      object.setProperty("inview-fallback", true);
      connectableModules.push(inviewFallbackModule);
    }
    if (this.domBatcherEnabled && connectableModules.length > 0) {
      const batchContexts = new Array(connectableModules.length);
      for (let i = 0; i < connectableModules.length; i++) {
        batchContexts[i] = {
          module: connectableModules[i],
          object,
          element: el,
          attributes,
          globalId: this.globalId,
          windowSize: this.getViewportSize(),
        };
      }
      this.domBatcher.batchModuleInitialization(batchContexts);
      this.domBatcher.scheduleWrite(() => {
        this.initObservers(object, el);
        this.checkInviewForObject(object);
      });
    } else {
      for (let i = 0; i < connectableModules.length; i++) {
        const m = connectableModules[i];
        m.initializeObject(this.globalId, object, el, attributes);
        m.calculatePositions(object, this.getViewportSize());
        m.connectObject(object);
        m.addObject(object.id, object);
      }
      this.initObservers(object, el);
      this.checkInviewForObject(object);
    }

    // Restore connect-from
    if (this.connectQueue.length > 0) {
      let write = 0;
      for (let i = 0; i < this.connectQueue.length; i++) {
        const item = this.connectQueue[i];
        if (item.id === object.id) {
          this.attachMirrorToObject(object, item.element);
          continue;
        }
        this.connectQueue[write++] = item;
      }
      this.connectQueue.length = write;
    }
    connectableModules.length = 0;
    this.inviewIndexDirty = true;
  }

  /**
   * Visible size of the active scroll container. Falls back to the window
   * for body-level scrolling, so custom containers (modals, panels) size
   * their scroll ranges against themselves rather than the viewport.
   */
  private getViewportSize(): number {
    return this.data.viewport.containerHeight || this.data.viewport.windowHeight;
  }

  public setDOMBatcherEnabled(enabled: boolean): void {
    this.domBatcherEnabled = enabled;
    if (!enabled) {
      this.domBatcher.flushSync();
    }
  }

  public setIntersectionObserverEnabled(enabled: boolean): void {
    if (this.intersectionObserverEnabled === enabled) return;
    this.intersectionObserverEnabled = enabled;
    for (const object of this.objects.values()) {
      object.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
      if (enabled) {
        this.initObservers(object, object.htmlElement);
      }
    }
  }

  public refreshObservers(): void {
    if (!this.intersectionObserverEnabled) {
      return;
    }

    for (const object of this.objects.values()) {
      const element = object.htmlElement;
      if (!element || !element.isConnected) continue;
      this.initObservers(object, element);
    }
  }

  public attachModule(module: StringModule): void {
    this.objects.forEach((object) => {
      if (!module.canConnect(object)) {
        return;
      }

      const element = object.htmlElement;
      const attributes = this.getAllAttributes(element);
      module.initializeObject(this.globalId, object, element, attributes);
      module.calculatePositions(object, this.getViewportSize());
      module.connectObject(object);
      module.addObject(object.id, object);
      if (object.getProperty<boolean>("is-entered") === true) {
        module.enterObject(object.id, object);
      }
    });
  }

  public refreshModuleConnectionsForCurrentMode(): void {
    const allModules = this.modules.all;

    for (const object of this.objects.values()) {
      const element = object.htmlElement;
      if (!element || !element.isConnected) continue;

      let attributes: Record<string, any> | null = null;

      for (let i = 0; i < allModules.length; i++) {
        const module = allModules[i];
        if (!(module instanceof StringModule)) continue;
        if (!object.keys.includes(module.key)) continue;

        const shouldConnect = module.canConnect(object);
        const isConnected = object.isConnectedTo(module);

        if (shouldConnect && !isConnected) {
          if (attributes == null) {
            attributes = this.getAllAttributes(element);
          }
          module.initializeObject(this.globalId, object, element, attributes);
          module.calculatePositions(object, this.getViewportSize());
          module.connectObject(object);
          module.addObject(object.id, object);
          if (object.getProperty<boolean>("is-entered") === true) {
            module.enterObject(object.id, object);
          }
          continue;
        }

        if (!shouldConnect && isConnected) {
          module.exitObject(object.id);
          module.removeObject(object.id);
          module.disconnectObject(object);
        }
      }
    }
  }

  public invalidateInviewIndex(): void {
    this.inviewIndexDirty = true;
  }

  public refreshLayoutForRoot(root: HTMLElement): void {
    if (!root) return;

    const targets = new Set<StringObject>();
    const maybeAdd = (el: HTMLElement) => {
      const id = el.getAttribute("string-id") ?? el.getAttribute("data-string-id");
      if (!id) return;
      const obj = this.objects.get(id);
      if (obj) targets.add(obj);
    };

    if (root instanceof HTMLElement) {
      maybeAdd(root);
      const nodes = root.querySelectorAll<HTMLElement>("[string-id],[data-string-id]");
      for (let i = 0; i < nodes.length; i++) {
        maybeAdd(nodes[i]);
      }
    }

    if (targets.size === 0) return;

    const windowSize = this.getViewportSize();
    for (const object of targets) {
      const element = object.htmlElement;
      if (!element || !element.isConnected) continue;
      const attributes = this.getAllAttributes(element);
      const all = this.modules.all;
      for (let i = 0; i < all.length; i++) {
        const m = all[i];
        if (!(m instanceof StringModule)) continue;
        if (!m.canConnect(object)) continue;
        m.initializeObject(this.globalId, object, element, attributes);
        m.calculatePositions(object, windowSize);
      }
    }

    this.inviewIndexDirty = true;
    this.checkInview();
  }

  /**
   * Removes an object by its id.
   */
  public remove(id: string) {
    const obj = this.objects.get(id);
    if (!obj) return;

    obj.events.clearAll();
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    obj.getProperty<IntersectionObserver>("observer-inview")?.disconnect();

    obj.htmlElement.removeAttribute("string-inited");
    obj.leave();
    obj.remove();

    obj.mirrorObjects.forEach((mirror) => {
      const mirrorIds = this.getMirrorIds(mirror.htmlElement);
      this.setMirrorIds(
        mirror.htmlElement,
        mirrorIds.filter((id) => id !== mirror.id)
      );
      this.mirrors.delete(mirror.id);

      const copyFromId =
        mirror.htmlElement.getAttribute("string-copy-from") ??
        mirror.htmlElement.getAttribute("data-string-copy-from");
      if (copyFromId) {
        this.enqueueConnection(copyFromId, mirror.htmlElement);
      }
    });

    this.objects.delete(id);
    this.inviewActive.delete(obj);
    this.inviewIndexDirty = true;
  }

  /**
   * Add an element that will connect later.
   */
  public enqueueConnection(id: string, element: HTMLElement) {
    // Split by | to handle multiple IDs
    const ids = this.splitPipeAndTrim(id);
    for (let i = 0; i < ids.length; i++) {
      const singleId = ids[i];
      if (this.connectQueue.some((item) => item.id === singleId && item.element === element)) {
        continue;
      }
      this.connectQueue.push({ id: singleId, element });
    }
  }

  public linkMirror(id: string, element: HTMLElement): void {
    // Split by | to handle multiple IDs
    const ids = this.splitPipeAndTrim(id);
    for (let i = 0; i < ids.length; i++) {
      const singleId = ids[i];
      const parent = this.objects.get(singleId);
      if (parent) {
        this.attachMirrorToObject(parent, element);
      } else {
        this.enqueueConnection(singleId, element);
      }
    }
  }

  private attachMirrorToObject(object: StringObject, element: HTMLElement): StringMirrorObject {
    const existingIds = this.getMirrorIds(element);
    for (const id of existingIds) {
      const existing = this.mirrors.get(id);
      if (existing && existing.parentObject === object) {
        return existing;
      }
    }

    const mirrorId = `string-mirror-${this.mirrorId++}`;
    const mirror = new StringMirrorObject(mirrorId, element, object);
    this.setMirrorIds(element, [...existingIds, mirrorId]);
    object.addMirror(mirror);
    this.mirrors.set(mirrorId, mirror);

    const easingAttr =
      element.getAttribute("string-easing") ?? element.getAttribute("data-string-easing");
    if (easingAttr && easingAttr.trim().length > 0) {
      mirror.setEasing(this.tools.easingFunction.process({ easing: easingAttr }));
      mirror.setProperty("easing", easingAttr);
    }

    const key = object.getProperty<string>("key");
    const rawProgress = object.getProperty<number>("progress-raw");
    const progressValue = object.getProperty<number>("progress-value");
    if (typeof rawProgress === "number") {
      const parentEasing = object.getProperty<(value: number) => number>("easing") ?? undefined;
      const mirrorProgress = mirror.applyProgress(rawProgress, parentEasing);
      mirror.setProperty("progress", mirrorProgress);
      if (key) {
        // Not inside a styleTxn transaction here (this runs from DOM-mutation
        // processing, not a per-frame styleTxn.run() block), so this must be
        // an immediate write, not setVar() — setVar() silently no-ops outside
        // an open transaction and this initial paint would never happen.
        this.tools.styleTxn.setVarDirect(mirror.htmlElement, key, mirrorProgress);
      }
    } else if (typeof progressValue === "number") {
      mirror.setProperty("progress", progressValue);
      if (key) {
        this.tools.styleTxn.setVarDirect(mirror.htmlElement, key, progressValue);
      }
    }

    return mirror;
  }

  private detachMirrorByElement(element: HTMLElement): void {
    const ids = this.getMirrorIds(element);
    if (ids.length === 0) return;
    ids.forEach((id) => this.detachMirrorById(id));
    this.clearMirrorIds(element);
  }

  private detachMirrorById(mirrorId: string): void {
    const mirror = this.mirrors.get(mirrorId);
    if (!mirror) return;
    mirror.parentObject.removeMirror(mirrorId);
    this.mirrors.delete(mirrorId);
  }

  private getMirrorIds(element: HTMLElement): string[] {
    const raw =
      element.getAttribute("string-mirror-id") ?? element.getAttribute("data-string-mirror-id");
    if (!raw) return [];
    return this.splitPipeAndTrim(raw);
  }

  private setMirrorIds(element: HTMLElement, ids: string[]): void {
    if (ids.length === 0) {
      this.clearMirrorIds(element);
      return;
    }
    element.setAttribute("string-mirror-id", ids.join("|"));
  }

  private clearMirrorIds(element: HTMLElement): void {
    element.removeAttribute("string-mirror-id");
    element.removeAttribute("data-string-mirror-id");
  }

  /**
   * Retrieves all attributes of a given HTML element and returns them as a record.
   *
   * @param el - The HTML element from which to extract attributes.
   * @returns A record where the keys are attribute names and the values are attribute values.
   */
  private getAllAttributes(el: HTMLElement): Record<string, any> {
    const attributes: Record<string, any> = {};
    const attrList = el.attributes;
    for (let i = 0; i < attrList.length; i++) {
      const attr = attrList[i];
      attributes[attr.name] = attr.value;
    }
    return attributes;
  }

  /**
   * Initializes IntersectionObservers for a given object and its associated HTML element.
   *
   * This method sets up observer:
   * - A "progress" observer to track when the object enters or leaves the viewport.
   *
   * The observers are configured with custom root margins and thresholds based on the object's properties.
   * Existing observers, if any, are disconnected before creating new ones.
   *
   * @param obj - The `StringObject` instance containing properties and methods for interaction.
   * @param el - The `HTMLElement` to observe for intersection changes.
   */
  private initObservers(obj: StringObject, el: HTMLElement) {
    if (!this.intersectionObserverEnabled) {
      return;
    }
    const start =
      obj.getProperty<number>("offset-exit") ??
      obj.getProperty<number>("offset-top") ??
      0;
    const end =
      obj.getProperty<number>("offset-enter") ??
      obj.getProperty<number>("offset-bottom") ??
      0;
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    const progressCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((e) => {
        this.events.emit(obj.getScopedEventName("object:activate"), e.isIntersecting);
        e.isIntersecting ? obj.enter() : obj.leave();
      });
    };
    const outsideProp = obj.getProperty<boolean>("outside-container");
    const outsideAttrValue =
      el.getAttribute("string-outside-container") ??
      el.getAttribute("data-string-outside-container");
    const outsideAttrNormalized =
      outsideAttrValue != null ? outsideAttrValue.trim().toLowerCase() : null;
    const outsideAttrFlag =
      outsideAttrNormalized === "" ||
      outsideAttrNormalized === "true" ||
      outsideAttrNormalized === "1";
    const isOutsideContainer = outsideProp != null ? outsideProp === true : outsideAttrFlag;
    const observerRoot =
      this.data.scroll.container === document.body || isOutsideContainer
        ? null
        : this.data.scroll.container;

    const progressObserver = new IntersectionObserver(progressCallback, {
      root: observerRoot,
      rootMargin: `${end + this.data.viewport.windowHeight}px 0px ${
        start + this.data.viewport.windowHeight
      }px 0px`,
      threshold: 0,
    });
    progressObserver.observe(el);
    obj.setProperty("observer-progress", progressObserver);
  }

  /**
   * Observes DOM mutations to auto-add/remove elements with [string] attribute.
   * Should be called once after DOM is ready.
   */
  public observeDOM(): void {
    this.domObserver?.disconnect();

    const observer = new MutationObserver((mutations) => {
      let shouldNotifyDOMRebuild = false;
      let stringElementChanged = false;

      for (let mutationIndex = 0; mutationIndex < mutations.length; mutationIndex++) {
        const mutation = mutations[mutationIndex];
        if (mutation.type === "childList") {
          let mutationHasElementNodes = false;

          // Removed elements
          for (let removedIndex = 0; removedIndex < mutation.removedNodes.length; removedIndex++) {
            const node = mutation.removedNodes[removedIndex];
            if (node.nodeType !== Node.ELEMENT_NODE) continue;

            const element = node as HTMLElement;
            if (this.isDevtoolsArtifact(element)) {
              continue;
            }

            mutationHasElementNodes = true;

            this.detachMirrorByElement(element);

            if (this.isFixed(element)) continue;

            if (element.hasAttribute("string") || element.hasAttribute("data-string")) {
              this.handleRemoved(element);
              stringElementChanged = true;
            }

            const queryStringNodes = element.querySelectorAll("[string],[data-string]");
            for (let i = 0; i < queryStringNodes.length; i++) {
              const child = queryStringNodes[i];
              if (this.isFixed(child as HTMLElement)) continue;
              this.handleRemoved(child as HTMLElement);
              stringElementChanged = true;
            }

            const queryMirrorNodes = element.querySelectorAll(
              "[string-copy-from],[data-string-copy-from]"
            );
            for (let i = 0; i < queryMirrorNodes.length; i++) {
              this.detachMirrorByElement(queryMirrorNodes[i] as HTMLElement);
            }
          }

          // Added elements
          for (let addedIndex = 0; addedIndex < mutation.addedNodes.length; addedIndex++) {
            const node = mutation.addedNodes[addedIndex];
            if (node.nodeType !== Node.ELEMENT_NODE) continue;

            const element = node as HTMLElement;
            if (this.isDevtoolsArtifact(element)) {
              continue;
            }

            mutationHasElementNodes = true;

            if (this.isFixed(element)) continue;

            if (element.hasAttribute("string") && !element.hasAttribute("string-inited")) {
              this.add(element);
              stringElementChanged = true;
            }

            const queryAddedStringNodes = element.querySelectorAll(
              "[string]:not([string-inited]),[data-string]:not([string-inited])"
            );
            for (let i = 0; i < queryAddedStringNodes.length; i++) {
              this.add(queryAddedStringNodes[i] as HTMLElement);
              stringElementChanged = true;
            }

            // Check for connect-from logic
            const copyFrom =
              element.getAttribute("string-copy-from") ??
              element.getAttribute("data-string-copy-from");

            if (copyFrom) {
              this.linkMirror(copyFrom, element);
            }

            const queryAddedMirrorNodes = element.querySelectorAll(
              "[string-copy-from],[data-string-copy-from]"
            );
            for (let i = 0; i < queryAddedMirrorNodes.length; i++) {
              const child = queryAddedMirrorNodes[i];
              const childCopyFrom =
                child.getAttribute("string-copy-from") ??
                child.getAttribute("data-string-copy-from");
              if (childCopyFrom) {
                this.linkMirror(childCopyFrom, child as HTMLElement);
              }
            }
          }

          if (mutationHasElementNodes) {
            this.modules.onDOMMutate(mutation.addedNodes, mutation.removedNodes);
            shouldNotifyDOMRebuild = true;
          }
        }
      }

      if (shouldNotifyDOMRebuild) {
        const all = this.modules.all;
        for (let i = 0; i < all.length; i++) {
          all[i].onDOMRebuild();
        }
        this.events.emit("dom:changed", { stringElementChanged });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    this.domObserver = observer;
  }

  /**
   * Removes an object and its observers.
   */
  private handleRemoved(el: HTMLElement): void {
    const id = el.getAttribute("string-id") ?? el.getAttribute("data-string-id");
    if (!id) return;

    const copyFrom =
      el.getAttribute("string-copy-from") ?? el.getAttribute("data-string-copy-from");
    if (copyFrom) {
      this.connectQueue = this.connectQueue.filter((q) => q.id !== copyFrom);
    }

    this.remove(id);
  }

  /**
   * Re-applies module initialization logic to all managed objects after settings change.
   *
   * This method should be called when `StringSettings` are updated at runtime,
   * especially if the new settings affect how modules calculate offsets,
   * easing, origins, or custom configuration.
   *
   * Internally, it re-runs `initializeObject`, `calculatePositions`, and `connectObject`
   * for each core module that can connect to the object.
   *
   * This is useful for supporting dynamic configuration updates without requiring
   * a full DOM rebuild or reinitialization.
   */
  public onSettingsChange(data: ISettingsChangeData) {
    for (const object of this.objects.values()) {
      if (!object.htmlElement || !object.htmlElement.isConnected) continue;

      let attributes: Record<string, any> | null = null;
      const all = this.modules.all;
      for (let i = 0; i < all.length; i++) {
        const m = all[i];
        let isCanRebuild = false;
        if (data.isDesktop) {
          if (m.permissions.desktop.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        } else {
          if (m.permissions.mobile.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        }

        if (isCanRebuild || data.isForceRebuild) {
          if (m.canConnect(object)) {
            if (attributes == null) {
              attributes = this.getAllAttributes(object.htmlElement);
            }
            m.initializeObject(this.globalId, object, object.htmlElement, attributes);
            m.calculatePositions(object, this.getViewportSize());
            m.connectObject(object);
          }
        }
      }
    }
    this.inviewIndexDirty = true;
  }

  /**
   * Checks whether the element is marked as fixed (not managed).
   */
  private isFixed(el: HTMLElement): boolean {
    return el.hasAttribute("string-fixed");
  }

  private isDevtoolsArtifact(el: HTMLElement): boolean {
    return el.closest(ObjectManager.DEVTOOLS_ARTIFACT_SELECTOR) != null;
  }

  public checkInview() {
    const scrollPos = this.data.scroll.transformedCurrent;
    this.updateInviewWindow(scrollPos);
    for (const object of this.inviewActive) {
      this.checkInviewForObject(object);
    }
  }

  private checkInviewForObject(object: StringObject) {
    const scrollPos = this.data.scroll.transformedCurrent;
    if (!this.intersectionObserverEnabled) {
      const activeStart = object.getProperty<number>("start-position");
      const activeEnd = object.getProperty<number>("end-position");
      if (activeStart != null && activeEnd != null) {
        const activeMin = Math.min(activeStart, activeEnd);
        const activeMax = Math.max(activeStart, activeEnd);
        const wasActive = object.getProperty<boolean>("is-active") ?? false;
        const isActive =
          scrollPos >= activeMin - ObjectManager.ACTIVE_RANGE_EPSILON &&
          scrollPos <= activeMax + ObjectManager.ACTIVE_RANGE_EPSILON;
        if (isActive !== wasActive) {
          object.setProperty("is-active", isActive);
          this.events.emit(object.getScopedEventName("object:activate"), isActive);
          isActive ? object.enter() : object.leave();
        }
      }
    }
    const inviewStart = object.getProperty<number>("inview-start-position");
    const inviewEnd = object.getProperty<number>("inview-end-position");
    const wasInView = object.getProperty<boolean>("is-inview") ?? false;

    const start = Math.min(inviewStart, inviewEnd);
    const end = Math.max(inviewStart, inviewEnd);
    const isNowInView = scrollPos >= start && scrollPos <= end;

    let direction: "enter-top" | "enter-bottom" | "exit-top" | "exit-bottom" | null = null;
    if (!wasInView && isNowInView) {
      const distToStart = Math.abs(scrollPos - start);
      const distToEnd = Math.abs(end - scrollPos);
      direction = distToStart <= distToEnd ? "enter-top" : "enter-bottom";
    } else if (wasInView && !isNowInView) {
      direction = scrollPos < start ? "exit-top" : "exit-bottom";
    }
    if (isNowInView !== wasInView) {
      object.setProperty("is-inview", isNowInView);
      object.setInviewAutoBlocked(false);
      object.setInviewManualActive(false);
      isNowInView ? object.show() : object.hide();
      this.events.emit(object.getScopedEventName("object:inview"), {
        inView: isNowInView,
        direction,
      });
    }
  }

  private updateInviewWindow(scrollPos: number) {
    const buffer = this.data.viewport.windowHeight;
    const windowMin = scrollPos - buffer;
    const windowMax = scrollPos + this.data.viewport.windowHeight + buffer;

    if (this.inviewIndexDirty) {
      this.rebuildInviewIndex(windowMin, windowMax);
    } else if (scrollPos < this.lastInviewScrollPos) {
      this.repositionInviewIndex(windowMin, windowMax);
    }

    while (
      this.inviewStartIdx < this.inviewStarts.length &&
      this.inviewStarts[this.inviewStartIdx].pos <= windowMax
    ) {
      this.inviewActive.add(this.inviewStarts[this.inviewStartIdx].object);
      this.inviewStartIdx++;
    }

    while (
      this.inviewEndIdx < this.inviewEnds.length &&
      this.inviewEnds[this.inviewEndIdx].pos < windowMin
    ) {
      this.inviewActive.delete(this.inviewEnds[this.inviewEndIdx].object);
      this.inviewEndIdx++;
    }

    this.lastInviewScrollPos = scrollPos;
  }

  private rebuildInviewIndex(windowMin: number, windowMax: number) {
    this.inviewStarts = [];
    this.inviewEnds = [];

    for (const object of this.objects.values()) {
      const start = object.getProperty<number>("inview-start-position");
      const end = object.getProperty<number>("inview-end-position");
      if (start == null || end == null) continue;
      this.inviewStarts.push({ pos: Math.min(start, end), object });
      this.inviewEnds.push({ pos: Math.max(start, end), object });
    }

    this.inviewStarts.sort((a, b) => a.pos - b.pos);
    this.inviewEnds.sort((a, b) => a.pos - b.pos);

    this.repositionInviewIndex(windowMin, windowMax);
    this.inviewIndexDirty = false;
  }

  private repositionInviewIndex(windowMin: number, windowMax: number) {
    this.inviewActive.clear();

    this.inviewStartIdx = this.upperBound(this.inviewStarts, windowMax);
    this.inviewEndIdx = this.upperBound(this.inviewEnds, windowMin - 1);

    for (let i = 0; i < this.inviewStartIdx; i++) {
      this.inviewActive.add(this.inviewStarts[i].object);
    }
    for (let i = 0; i < this.inviewEndIdx; i++) {
      this.inviewActive.delete(this.inviewEnds[i].object);
    }
  }

  private upperBound(list: Array<{ pos: number }>, value: number): number {
    let low = 0;
    let high = list.length;
    while (low < high) {
      const mid = (low + high) >>> 1;
      if (list[mid].pos <= value) low = mid + 1;
      else high = mid;
    }
    return low;
  }

  private splitPipeAndTrim(raw: string): string[] {
    const parts = raw.split("|");
    const result: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      const item = parts[i].trim();
      if (item.length > 0) {
        result.push(item);
      }
    }
    return result;
  }

  private parseStringTokens(raw: string): StringToken[] {
    const chunks = this.splitTopLevelPipe(raw);
    const tokens: StringToken[] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i].trim();
      if (chunk.length === 0) continue;

      const match = chunk.match(/^([^\[\]]+?)(?:\[([^\]]*)\])?$/);
      if (!match) {
        tokens.push({
          raw: chunk,
          key: chunk,
          modeSpec: { kind: "default", values: [] },
        });
        continue;
      }

      const key = match[1].trim();
      const modeRaw = match[2];

      if (!key) {
        continue;
      }

      if (modeRaw == null) {
        tokens.push({
          raw: chunk,
          key,
          modeSpec: { kind: "default", values: [] },
        });
        continue;
      }

      const normalizedModeRaw = modeRaw.trim();
      if (normalizedModeRaw.length === 0) {
        tokens.push({
          raw: chunk,
          key,
          modeSpec: { kind: "all", values: [] },
        });
        continue;
      }

      const values = this.splitTopLevelPipe(normalizedModeRaw)
        .map((value) => value.trim())
        .filter((value) => value.length > 0) as ScrollMode[];

      tokens.push({
        raw: chunk,
        key,
        modeSpec: values.length > 0
          ? { kind: "include", values }
          : { kind: "all", values: [] },
      });
    }

    return tokens;
  }

  private splitTopLevelPipe(raw: string): string[] {
    const result: string[] = [];
    let current = "";
    let bracketDepth = 0;

    for (let i = 0; i < raw.length; i++) {
      const char = raw[i];

      if (char === "[") {
        bracketDepth++;
        current += char;
        continue;
      }

      if (char === "]") {
        bracketDepth = Math.max(0, bracketDepth - 1);
        current += char;
        continue;
      }

      if (char === "|" && bracketDepth === 0) {
        result.push(current);
        current = "";
        continue;
      }

      current += char;
    }

    if (current.length > 0) {
      result.push(current);
    }

    return result;
  }

  public destroy(): void {
    this.domObserver?.disconnect();
    this.domObserver = null;
    this.domBatcher.clear();
  }
}
